import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, RegisterData } from "../hooks/useAuth";
import toast from "react-hot-toast";
import { APP_NAME, APP_TAGLINE } from "../constants/expiry";
import { COUNTRIES, TIMEZONES, detectBrowserTimezone } from "../constants/geo";
import { SentrilogIcon } from "../components/Icons";

// ── Step types ─────────────────────────────────────────────────────────────────
type Step = "email" | "magic-link-sent" | "register";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/items";
  const { checkEmail, requestMagicLink, register, isAuthenticated } = useAuth();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [timezone, setTimezone] = useState(() => detectBrowserTimezone());
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);

  // Auto-update timezone when country changes.
  // The select value is the ISO 3166-1 alpha-2 code ("US", "IN", …) which is
  // what gets stored in the DB. The option label still shows the full country name.
  const handleCountryChange = (code: string) => {
    setCountry(code);
    const found = COUNTRIES.find((c) => c.code === code);
    if (found) setTimezone(found.timezone);
  };

  // Already authenticated → redirect (honours ?redirect= param for invite flow)
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  // ── Step 1: check email ──────────────────────────────────────────────────────
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      toast.error("Please enter your email");
      return;
    }

    setChecking(true);
    try {
      const { exists } = await checkEmail(trimmed);
      if (exists) {
        // Returning user — request magic link (demo_users auto-login without email)
        const result = await requestMagicLink(trimmed);
        if (result.autoLoggedIn) {
          navigate(redirectTo, { replace: true });
        } else {
          // Persist the intended destination so VerifyMagicLink can pick it up
          if (redirectTo !== "/items") {
            localStorage.setItem("post_login_redirect", redirectTo);
          }
          setStep("magic-link-sent");
        }
      } else {
        // New user — show registration form
        setStep("register");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Something went wrong");
    } finally {
      setChecking(false);
    }
  };

  // ── Step 2a: resend magic link ────────────────────────────────────────────────
  const handleResend = async () => {
    setLoading(true);
    try {
      await requestMagicLink(email.trim().toLowerCase());
      toast.success("A new sign-in link has been sent!");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2b: register new user ────────────────────────────────────────────────
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    if (!lastName.trim()) {
      toast.error("Last name is required");
      return;
    }

    setLoading(true);
    try {
      const data: RegisterData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        company_name: companyName.trim() || undefined,
        email: email.trim().toLowerCase(),
        country: country || undefined,
        timezone: timezone || undefined,
      };
      await register(data);
      toast.success("Account created! Check your email for a sign-in link.");
      // Persist the intended destination so VerifyMagicLink can pick it up
      if (redirectTo !== "/items") {
        localStorage.setItem("post_login_redirect", redirectTo);
      }
      setStep("magic-link-sent");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setFirstName("");
    setLastName("");
    setCompanyName("");
    setCountry("");
    setTimezone(detectBrowserTimezone());
  };

  // ── Reusable UI pieces ────────────────────────────────────────────────────────
  const PageIcon = () => (
    <div className="flex justify-center mb-4">
      <div className="h-12 w-12 bg-[#2563EB] rounded-xl flex items-center justify-center shadow-md shadow-blue-500/30">
        <SentrilogIcon className="h-7 w-7 text-white" />
      </div>
    </div>
  );

  const InputClass =
    "appearance-none block w-full px-4 py-3 border border-[#E5E7EB] " +
    "placeholder:text-[#9CA3AF] text-[#111827] rounded-lg text-sm transition-colors " +
    "focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent";

  // Selects need appearance-none to style cross-browser, but also need the
  // custom caret injected via background-image so the arrow stays visible.
  const SelectClass =
    "appearance-none block w-full px-4 py-3 pr-10 border border-[#E5E7EB] " +
    "text-[#111827] bg-white rounded-lg text-sm transition-colors cursor-pointer " +
    "focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent " +
    "bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M1 1l5 5 5-5'/%3E%3C/svg%3E\")] " +
    "bg-no-repeat bg-[right_14px_center]";

  const PrimaryBtn = ({
    label,
    disabled,
  }: {
    label: string;
    disabled?: boolean;
  }) => (
    <button
      type="submit"
      disabled={disabled}
      className="w-full py-3 px-4 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50
                 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg
                 transition-colors flex items-center justify-center gap-2"
    >
      {disabled && (
        <svg
          className="animate-spin h-4 w-4 text-white"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {label}
    </button>
  );

  const BackBtn = () => (
    <button
      type="button"
      onClick={handleBack}
      className="w-full py-3 px-4 border border-[#E5E7EB] rounded-lg text-sm font-medium
                 text-[#374151] bg-white hover:bg-[#F9FAFB] transition-colors"
    >
      ← Use a different email
    </button>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        {/* ── Email step ────────────────────────────────────────────────── */}
        {step === "email" && (
          <>
            <div className="text-center">
              <PageIcon />
              <h2 className="text-3xl font-bold text-[#111827]">{APP_NAME}</h2>
              <p className="mt-1 text-sm font-semibold text-[#2563EB]">
                {APP_TAGLINE}
              </p>
              <p className="mt-2 text-sm text-[#6B7280]">
                Enter your email to get started — no password needed
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-8">
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Email address
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    className={InputClass}
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <PrimaryBtn
                  label={checking ? "Checking..." : "Continue"}
                  disabled={checking}
                />
              </form>
            </div>

            <p className="text-center text-xs text-[#9CA3AF]">
              We'll send you a magic link — no passwords ever.
            </p>
          </>
        )}

        {/* ── Magic link sent ──────────────────────────────────────────── */}
        {step === "magic-link-sent" && (
          <>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-[#DCFCE7] rounded-full flex items-center justify-center">
                  <svg
                    className="h-8 w-8 text-[#16A34A]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-[#111827]">
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-[#6B7280]">
                We sent a sign-in link to
              </p>
              <p className="font-semibold text-[#111827] mt-1">{email}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-8 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-[#F0FDF4] rounded-xl border border-[#BBF7D0]">
                <svg
                  className="h-5 w-5 text-[#16A34A] mt-0.5 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-[#15803D]">
                  The link expires in <strong>15 minutes</strong> and can only
                  be used once. Check your spam folder if you don't see it.
                </p>
              </div>

              <button
                onClick={handleResend}
                disabled={loading}
                className="w-full py-3 px-4 border border-[#E5E7EB] rounded-lg text-sm
                           font-medium text-[#374151] bg-white hover:bg-[#F9FAFB]
                           disabled:opacity-50 transition-colors"
              >
                {loading ? "Sending..." : "Resend sign-in link"}
              </button>

              <BackBtn />
            </div>
          </>
        )}

        {/* ── Register step ────────────────────────────────────────────── */}
        {step === "register" && (
          <>
            <div className="text-center">
              <PageIcon />
              <h2 className="text-2xl font-bold text-[#111827]">
                Create your account
              </h2>
              <p className="mt-2 text-sm text-[#6B7280]">
                Tell us a bit about yourself to get started
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-8">
              <form onSubmit={handleRegister} className="space-y-5">
                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="appearance-none block w-full px-4 py-3 border border-[#E5E7EB]
                               bg-[#F3F4F6] text-[#9CA3AF] rounded-lg text-sm cursor-not-allowed"
                  />
                </div>

                {/* Name row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      autoFocus
                      className={InputClass}
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      className={InputClass}
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Company name */}
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Company name{" "}
                    <span className="text-[#9CA3AF] font-normal">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    className={InputClass}
                    placeholder="Acme Inc."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Country{" "}
                    <span className="text-[#9CA3AF] font-normal">
                      (optional)
                    </span>
                  </label>
                  <select
                    className={SelectClass}
                    value={country}
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    <option value="">— Select country —</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Timezone */}
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Timezone
                  </label>
                  <select
                    className={SelectClass}
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[#9CA3AF]">
                    Auto-detected from your browser. Changes with country
                    selection.
                  </p>
                </div>

                <div className="flex flex-col gap-3 pt-1">
                  <PrimaryBtn
                    label={
                      loading
                        ? "Creating account..."
                        : "Create Account & Sign in"
                    }
                    disabled={loading}
                  />
                  <BackBtn />
                </div>

                <p className="text-center text-xs text-[#9CA3AF]">
                  By creating an account you agree to our Terms of Service and
                  Privacy Policy.
                </p>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
